import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Calendar, Users, DollarSign, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import TopBar from "@/components/TopBar";
import EventStatusBadge from "@/components/EventStatusBadge";
import EventsFilterBar from "@/components/EventsFilterBar";
import useEventFilters from "@/lib/useEventFilters";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const ROLE_REDIRECT = {
  operations: "/admin/operations",
  logistics:  "/admin/logistics",
  commercial: "/admin/commercial",
  finance:    "/admin/finance",
  hr:         "/admin/hr",
};

export default function AdminDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auto-route non-super_admin roles to their dedicated dashboard.
  useEffect(() => {
    if (user && user !== false && user.role && user.role !== "super_admin" && user.role !== "admin") {
      const dest = ROLE_REDIRECT[user.role];
      if (dest) navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/admin/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Split upcoming/live vs completed/archived vs cancelled (soft-deleted)
  const upcoming = events.filter((e) => e.status === "upcoming" || e.status === "live");
  const past = events.filter((e) => e.status === "completed" || e.status === "archived");
  const cancelled = events.filter((e) => e.status === "cancelled");

  const renderCard = (ev) => (
    <Link
      key={ev.id}
      to={`/admin/events/${ev.id}`}
      data-testid={`event-row-${ev.id}`}
      className="group bg-white rounded-2xl p-6 border border-[#F2E2EC] hover:border-[#D81B8C]/50 transition-colors block"
    >
      <div className="flex justify-between gap-3 mb-3">
        <h3 className="font-display text-xl font-black text-[#2A0E3C] leading-tight line-clamp-2">
          {ev.title}
        </h3>
        <ChevronRight className="w-5 h-5 text-[#5C4664] group-hover:text-[#D81B8C] mt-1 shrink-0" />
      </div>
      <div className="text-sm text-[#5C4664] mb-3">{ev.host_names}</div>
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-[#5C4664]">{t("admin.code")}</span>
          <span className="font-mono font-bold text-[#D81B8C] text-lg tracking-widest">
            {ev.code}
          </span>
        </div>
        <EventStatusBadge status={ev.status || "upcoming"} />
      </div>
      <div className="grid grid-cols-3 gap-2 pt-4 border-t border-[#F2E2EC] text-xs">
        <div>
          <Calendar className="w-3.5 h-3.5 text-[#5C4664] mb-1" />
          <div className="font-medium text-[#2A0E3C]">{ev.event_date}</div>
        </div>
        <div>
          <Users className="w-3.5 h-3.5 text-[#5C4664] mb-1" />
          <div className="font-medium text-[#2A0E3C]">
            {ev.contributors_count || 0}
          </div>
        </div>
        <div>
          <DollarSign className="w-3.5 h-3.5 text-[#5C4664] mb-1" />
          <div className="font-medium text-[#2A0E3C]">
            ${(ev.total_collected || 0).toFixed(0)}
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#FFF9FB]">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5 pb-24">
        <div className="flex items-end justify-between mt-6 flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-2">
              {t("admin.dashboard")}
            </p>
            <h1 className="font-display text-3xl sm:text-5xl font-black text-[#2A0E3C]">
              Administrateur
            </h1>
            {user && (
              <p className="mt-2 text-sm text-[#5C4664]">
                {user.email}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/suppliers")}
              data-testid="suppliers-link"
              className="rounded-full border-[#F2E2EC] hover:border-[#6B2C8F] gap-2"
              title="Référentiel fournisseurs Milkado (catalogue + dépenses)"
            >
              Fournisseurs
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/settings")}
              data-testid="settings-link"
              className="rounded-full border-[#F2E2EC] hover:border-[#D81B8C] gap-2"
            >
              {t("admin.settings")}
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="rounded-full border-[#F2E2EC] hover:border-[#D81B8C] gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t("common.logout")}
            </Button>
          </div>
        </div>

        <div className="mt-10">
          {/* Modules équipe (super_admin / admin only) */}
          <section data-testid="team-modules-section" className="mb-10">
            <h2 className="font-display text-xl font-black text-[#2A0E3C] mb-4">
              Modules équipe
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Link to="/admin/commercial" data-testid="module-commercial" className="bg-white rounded-2xl border border-[#F2E2EC] hover:border-[#D81B8C] p-4 transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF8A00]">Commercial</div>
                <div className="font-display font-black text-[#2A0E3C] mt-1">Pipeline & ventes</div>
              </Link>
              <Link to="/admin/operations" data-testid="module-operations" className="bg-white rounded-2xl border border-[#F2E2EC] hover:border-[#D81B8C] p-4 transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B2C8F]">Opérations</div>
                <div className="font-display font-black text-[#2A0E3C] mt-1">Achats & catalogue</div>
              </Link>
              <Link to="/admin/logistics" data-testid="module-logistics" className="bg-white rounded-2xl border border-[#F2E2EC] hover:border-[#D81B8C] p-4 transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#206F18]">Logistique</div>
                <div className="font-display font-black text-[#2A0E3C] mt-1">Stockage & livraisons</div>
              </Link>
              <Link to="/admin/finance" data-testid="module-finance" className="bg-white rounded-2xl border border-[#F2E2EC] hover:border-[#D81B8C] p-4 transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#3FA535]">Finances</div>
                <div className="font-display font-black text-[#2A0E3C] mt-1">Trésorerie</div>
              </Link>
              <Link to="/admin/hr" data-testid="module-hr" className="bg-white rounded-2xl border border-[#F2E2EC] hover:border-[#D81B8C] p-4 transition-colors">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#206f8b]">RH</div>
                <div className="font-display font-black text-[#2A0E3C] mt-1">Équipe Milkado</div>
              </Link>
            </div>
          </section>

          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : events.length === 0 ? (
            <div data-testid="no-events" className="py-16 text-center text-[#5C4664]">
              {t("admin.noEvents")}
            </div>
          ) : (
            <>
              {/* Upcoming / Live */}
              <UpcomingSection events={upcoming} renderCard={renderCard} title={t("admin.upcomingEvents")} />

              {/* Past */}
              {past.length > 0 && (
                <section data-testid="past-events-section" className="mt-4">
                  <h2 className="font-display text-xl font-black text-[#5C4664] mb-4">
                    {t("admin.pastEvents")}{" "}
                    <span className="font-normal text-base">({past.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-90">
                    {past.map(renderCard)}
                  </div>
                </section>
              )}

              {/* Cancelled (soft-deleted, read-only) */}
              {cancelled.length > 0 && (
                <section data-testid="cancelled-events-section" className="mt-10">
                  <h2 className="font-display text-xl font-black text-red-700 mb-4">
                    {t("admin.cancelledEvents")}{" "}
                    <span className="font-normal text-base text-[#5C4664]">({cancelled.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-80">
                    {cancelled.map(renderCard)}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function UpcomingSection({ events, renderCard, title }) {
  const alertedTotal = events.filter((e) => e.logistics_alert).length;
  const f = useEventFilters(events);
  return (
    <section data-testid="upcoming-events-section" className="mb-12">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-display text-xl font-black text-[#2A0E3C]">
          {title}{" "}
          <span className="text-[#5C4664] font-normal text-base">({events.length})</span>
        </h2>
      </div>
      {events.length === 0 ? (
        <div className="text-sm text-[#5C4664]">—</div>
      ) : (
        <>
          <EventsFilterBar
            search={f.search} setSearch={f.setSearch}
            alertOnly={f.alertOnly} setAlertOnly={f.setAlertOnly}
            range={f.range} setRange={f.setRange}
            alertCount={alertedTotal}
            totalCount={events.length}
            filteredCount={f.filtered.length}
            className="mb-4"
          />
          {f.filtered.length === 0 ? (
            <p data-testid="upcoming-no-match" className="text-sm text-[#5C4664]">
              Aucun événement ne correspond à ce filtre.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {f.visible.map(renderCard)}
            </div>
          )}
          {f.hiddenCount > 0 && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => f.setExpanded(true)}
                data-testid="upcoming-expand"
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-white border border-[#F2E2EC] text-sm font-semibold text-[#2A0E3C] hover:border-[#D81B8C]"
              >
                Voir tous ({f.filtered.length}) <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
          {f.expanded && f.filtered.length > f.pageSize && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => f.setExpanded(false)}
                data-testid="upcoming-collapse"
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold text-[#5C4664] hover:text-[#2A0E3C]"
              >
                Réduire <ChevronUp className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
